import React, { useMemo, useState } from 'react';
import {
  Box,
  Chip,
  Divider,
  IconButton,
  Popover,
  Tooltip,
  Typography,
  makeStyles,
  createStyles,
} from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { format, isBefore, startOfDay } from 'date-fns';
import { GridSelectionModel } from '@mui/x-data-grid-pro';
import { CurrencyDisplay, DataTable, DateDisplay, IDataTableColumn, Link } from '@lib/common';
import { BulkChangeBillingPreviewModel } from '@lib/state/api/generated/reservations';
import { CHIP_SMALL, cellNoWrapStyle, rowLevelBorderStyle } from '../bulk-change-billing-styles';
import {
  PreviewFilter,
  isRowBlocked,
  matchesFilter,
  formatSignedCurrency,
  getRowStatus,
} from '../bulk-change-billing-utils';

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const useStyles = makeStyles(theme =>
  createStyles({
    // Table cells
    cellNoWrap: cellNoWrapStyle,
    currencyCell: {
      whiteSpace: 'nowrap',
      textAlign: 'right',
      fontVariantNumeric: 'tabular-nums',
    },
    // Rate columns: amount + plan name
    ratePrimary: {
      fontWeight: 500,
      whiteSpace: 'nowrap',
    },
    rateSecondary: {
      fontSize: '0.7rem',
      color: theme.palette.text.secondary,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: 120,
    },
    // Net difference color coding
    netPositive: {
      color: theme.palette.success?.main ?? '#388e3c',
      fontWeight: 700,
    },
    netNegative: {
      color: theme.palette.error?.main ?? '#d32f2f',
      fontWeight: 700,
    },
    netZero: {
      color: theme.palette.text.secondary,
      fontWeight: 600,
    },
    // Status badges
    badgeReady: {
      ...CHIP_SMALL,
      backgroundColor: theme.palette.success?.main ?? '#4caf50',
      color: '#fff',
    },
    badgeIncrease: {
      ...CHIP_SMALL,
      backgroundColor: theme.palette.success?.main ?? '#388e3c',
      color: '#fff',
    },
    badgeDecrease: {
      ...CHIP_SMALL,
      backgroundColor: theme.palette.error?.main ?? '#d32f2f',
      color: '#fff',
    },
    badgeBlocked: {
      ...CHIP_SMALL,
      backgroundColor: theme.palette.grey[500],
      color: '#fff',
    },
    statusDetail: {
      fontSize: '0.7rem',
      color: theme.palette.text.secondary,
      maxWidth: 140,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    delegateChangedIcon: {
      marginLeft: theme.spacing(0.5),
      color: theme.palette.primary.main,
      fontSize: '0.85rem',
      verticalAlign: 'middle',
      cursor: 'default',
    },
    legend: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.75),
      marginTop: theme.spacing(1),
      fontSize: '0.75rem',
      color: theme.palette.text.secondary,
    },
    legendIcon: {
      color: theme.palette.primary.main,
      fontSize: '0.85rem',
    },
    // Blocked row styling
    blockedRow: {
      opacity: 0.55,
      backgroundColor: theme.palette.action.disabledBackground,
    },
    // Preview table: align cell borders at row level
    previewTable: rowLevelBorderStyle(theme),
    // "View all rate plans" trigger
    rateMoreButton: {
      padding: 2,
      marginLeft: theme.spacing(0.5),
      fontSize: '0.7rem',
      color: theme.palette.primary.main,
    },
    rateCellWithButton: {
      display: 'inline-flex',
      alignItems: 'flex-start',
      gap: theme.spacing(0.5),
    },
    // Popover content
    popoverPaper: {
      padding: theme.spacing(1.5, 2),
      minWidth: 280,
      maxWidth: 360,
    },
    popoverTitle: {
      fontWeight: 600,
      marginBottom: theme.spacing(0.5),
    },
    popoverList: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(0.5),
      marginTop: theme.spacing(1),
    },
    popoverRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      gap: theme.spacing(2),
      padding: theme.spacing(0.25, 0),
      fontVariantNumeric: 'tabular-nums',
    },
    popoverRowFuture: {
      color: theme.palette.primary.main,
      fontWeight: 500,
    },
    popoverRowInRange: {
      color: theme.palette.success?.main ?? '#388e3c',
      fontWeight: 700,
    },
    popoverRowPast: {
      color: theme.palette.text.disabled,
    },
    popoverGroupLabel: {
      fontSize: '0.65rem',
      letterSpacing: 0.5,
      textTransform: 'uppercase',
      color: theme.palette.text.secondary,
      marginTop: theme.spacing(0.5),
    },
  })
);

// ---------------------------------------------------------------------------
// Cell renderers (small FCs — avoids recreating closures each render)
// ---------------------------------------------------------------------------

/** Currency cell; blocked rows show em-dash to indicate "not evaluated" */
const CurrencyCell: React.FC<{ row: BulkChangeBillingPreviewModel; value?: number | null }> = ({
  row,
  value,
}) => {
  const styles = useStyles();
  if (isRowBlocked(row)) {
    return (
      <Typography variant="body2" className={styles.currencyCell}>
        —
      </Typography>
    );
  }
  return <CurrencyDisplay value={value} variant="body2" className={styles.currencyCell} />;
};

/** Net cell: blocked → em-dash, otherwise signed + color-coded */
const NetCell: React.FC<{ row: BulkChangeBillingPreviewModel }> = ({ row }) => {
  const styles = useStyles();
  if (isRowBlocked(row)) {
    return (
      <Typography variant="body2" className={`${styles.currencyCell} ${styles.netZero}`}>
        —
      </Typography>
    );
  }
  const value = row.netDifference ?? 0;
  const cls = value > 0 ? styles.netPositive : value < 0 ? styles.netNegative : styles.netZero;
  return (
    <Typography variant="body2" className={`${styles.currencyCell} ${cls}`}>
      {formatSignedCurrency(value)}
    </Typography>
  );
};

/**
 * New Rates cell: shows the first entry from `rateChangeSummaries` that overlaps the
 * effective window (`isInEffectiveRange === true`) — the new standard rate amount and
 * the date range it applies to. Rows whose first in-range summary is missing render an
 * em-dash to indicate that nothing changes within the selected effective window.
 *
 * When the row has any rate change summaries, a small icon button is rendered that
 * opens a popover showing every summary, classified relative to the run's effective
 * start date into three groups: in-range (intersects), after, and before.
 */
const NewRatesCell: React.FC<{
  row: BulkChangeBillingPreviewModel;
  effectiveStartDate?: Date;
}> = ({ row, effectiveStartDate }) => {
  const styles = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  if (isRowBlocked(row)) {
    return (
      <Typography variant="body2" className={styles.currencyCell}>
        —
      </Typography>
    );
  }

  const summaries = row.rateChangeSummaries ?? [];
  const inRangeSummary = summaries.find(s => s.isInEffectiveRange === true);
  const hasSummaries = summaries.length > 0;

  // Classify each summary relative to the run's effective start date:
  //  - inRange  : isInEffectiveRange === true (intersects the window)
  //  - after    : not in-range AND endDate is on/after the effective start (entirely upcoming)
  //  - before   : endDate is strictly before the effective start (entirely past)
  // If no effective start date was provided we fall back to today.
  const cutoff = startOfDay(effectiveStartDate ?? new Date());
  const isBeforeCutoff = (dateStr?: string) => !!dateStr && isBefore(new Date(dateStr), cutoff);

  const inRangeSummaries = summaries
    .filter(s => s.isInEffectiveRange === true)
    .sort((a, b) => (a.startDate ?? '').localeCompare(b.startDate ?? ''));
  const afterSummaries = summaries
    .filter(s => s.isInEffectiveRange !== true && !isBeforeCutoff(s.endDate))
    .sort((a, b) => (a.startDate ?? '').localeCompare(b.startDate ?? ''));
  const beforeSummaries = summaries
    .filter(s => s.isInEffectiveRange !== true && isBeforeCutoff(s.endDate))
    .sort((a, b) => (b.startDate ?? '').localeCompare(a.startDate ?? ''));

  return (
    <>
      {inRangeSummary ? (
        <div className={styles.rateCellWithButton}>
          <Tooltip
            title={
              inRangeSummary.newRateName
                ? `${inRangeSummary.newRateName} — applies within the effective date range selected for this run.`
                : 'Applies within the effective date range selected for this run.'
            }
            placement="top"
            arrow
          >
            <div>
              <CurrencyDisplay
                value={inRangeSummary.newStandardRate}
                variant="body2"
                className={styles.ratePrimary}
              />
              <Typography className={styles.rateSecondary} component="div">
                {inRangeSummary.startDate ? format(new Date(inRangeSummary.startDate), 'M/d') : '—'}
                {' – '}
                {inRangeSummary.endDate ? format(new Date(inRangeSummary.endDate), 'M/d') : '—'}
              </Typography>
            </div>
          </Tooltip>
          {hasSummaries && (
            <Tooltip title="View all rate plans for this reservation" placement="top" arrow>
              <IconButton
                size="small"
                className={styles.rateMoreButton}
                onClick={e => setAnchorEl(e.currentTarget)}
                aria-label="View all rate plans"
              >
                <FontAwesomeIcon icon="info-circle" />
              </IconButton>
            </Tooltip>
          )}
        </div>
      ) : (
        <div className={styles.rateCellWithButton}>
          <Typography variant="body2" className={styles.currencyCell}>
            —
          </Typography>
          {hasSummaries && (
            <Tooltip title="View all rate plans for this reservation" placement="top" arrow>
              <IconButton
                size="small"
                className={styles.rateMoreButton}
                onClick={e => setAnchorEl(e.currentTarget)}
                aria-label="View all rate plans"
              >
                <FontAwesomeIcon icon="info-circle" />
              </IconButton>
            </Tooltip>
          )}
        </div>
      )}

      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ className: styles.popoverPaper }}
      >
        <Typography variant="subtitle2" className={styles.popoverTitle}>
          Rate Plans for R{row.recordNumber ?? '—'}
        </Typography>
        <Typography variant="caption" color="textSecondary" component="div">
          Rates that intersect the effective range are highlighted; rates before the effective start
          date are dimmed.
        </Typography>

        {inRangeSummaries.length > 0 && (
          <>
            <Typography className={styles.popoverGroupLabel}>In effective range</Typography>
            <Box className={styles.popoverList}>
              {inRangeSummaries.map((s, i) => (
                <div key={`i-${i}`} className={`${styles.popoverRow} ${styles.popoverRowInRange}`}>
                  <span>
                    {s.startDate ? format(new Date(s.startDate), 'M/d/yyyy') : '—'}
                    {' – '}
                    {s.endDate ? format(new Date(s.endDate), 'M/d/yyyy') : '—'}
                  </span>
                  <CurrencyDisplay value={s.newStandardRate} variant="body2" />
                </div>
              ))}
            </Box>
          </>
        )}

        {inRangeSummaries.length > 0 &&
          (afterSummaries.length > 0 || beforeSummaries.length > 0) && (
            <Divider style={{ marginTop: 8, marginBottom: 4 }} />
          )}

        {afterSummaries.length > 0 && (
          <>
            <Typography className={styles.popoverGroupLabel}>After effective range</Typography>
            <Box className={styles.popoverList}>
              {afterSummaries.map((s, i) => (
                <div key={`f-${i}`} className={`${styles.popoverRow} ${styles.popoverRowFuture}`}>
                  <span>
                    {s.startDate ? format(new Date(s.startDate), 'M/d/yyyy') : '—'}
                    {' – '}
                    {s.endDate ? format(new Date(s.endDate), 'M/d/yyyy') : '—'}
                  </span>
                  <CurrencyDisplay value={s.newStandardRate} variant="body2" />
                </div>
              ))}
            </Box>
          </>
        )}

        {afterSummaries.length > 0 && beforeSummaries.length > 0 && (
          <Divider style={{ marginTop: 8, marginBottom: 4 }} />
        )}

        {beforeSummaries.length > 0 && (
          <>
            <Typography className={styles.popoverGroupLabel}>Before effective range</Typography>
            <Box className={styles.popoverList}>
              {beforeSummaries.map((s, i) => (
                <div key={`p-${i}`} className={`${styles.popoverRow} ${styles.popoverRowPast}`}>
                  <span>
                    {s.startDate ? format(new Date(s.startDate), 'M/d/yyyy') : '—'}
                    {' – '}
                    {s.endDate ? format(new Date(s.endDate), 'M/d/yyyy') : '—'}
                  </span>
                  <CurrencyDisplay value={s.newStandardRate} variant="body2" />
                </div>
              ))}
            </Box>
          </>
        )}
      </Popover>
    </>
  );
};

/** Status badge + short detail text with full-detail tooltip */
const StatusCell: React.FC<{ row: BulkChangeBillingPreviewModel }> = ({ row }) => {
  const styles = useStyles();
  const status = getRowStatus(row);
  const badgeClass =
    status.variant === 'blocked'
      ? styles.badgeBlocked
      : status.variant === 'increase'
      ? styles.badgeIncrease
      : status.variant === 'decrease'
      ? styles.badgeDecrease
      : styles.badgeReady;

  const delegateChanged = !isRowBlocked(row) && row.delegateWasReplaced === true;
  const delegateTooltip = delegateChanged
    ? `Delegate will change${
        row.oldDelegateName || row.newDelegateName
          ? `: ${row.oldDelegateName ?? '—'} → ${row.newDelegateName ?? '—'}`
          : ''
      }`
    : '';

  return (
    <div>
      <Tooltip title={status.detail} placement="left" arrow>
        <Chip label={status.label} className={badgeClass} size="small" />
      </Tooltip>
      {delegateChanged && (
        <Tooltip title={delegateTooltip} placement="left" arrow>
          <span className={styles.delegateChangedIcon}>
            <FontAwesomeIcon icon="user-edit" aria-label="Delegate changed" />
          </span>
        </Tooltip>
      )}
      {status.variant !== 'ready' && (
        <Typography className={styles.statusDetail} component="div">
          {status.shortText}
        </Typography>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export interface PreviewTableProps {
  rows: BulkChangeBillingPreviewModel[];
  filter: PreviewFilter;
  /** Effective start date selected for the run; used to classify rate change summaries. */
  effectiveStartDate?: Date;
  /** Currently-selected eligible reservation IDs. Owned by the parent dialog. */
  selectedIds: Set<string>;
  /** Called with a new Set whenever the user toggles row selection. */
  onSelectionChange: (next: Set<string>) => void;
}

export const PreviewTable: React.FC<PreviewTableProps> = ({
  rows,
  filter,
  effectiveStartDate,
  selectedIds,
  onSelectionChange,
}) => {
  const styles = useStyles();

  const filteredRows = useMemo(
    () =>
      rows
        .filter(r => matchesFilter(r, filter))
        .sort((a, b) => (b.recordNumber ?? 0) - (a.recordNumber ?? 0)),
    [rows, filter]
  );

  // Selection model passed to the grid only includes IDs whose row is currently visible.
  // Sending hidden IDs would cause DataGridPro to fire onSelectionModelChange to drop them,
  // which combined with our preserve-hidden merge would cause an infinite render loop.
  // (Same pattern as bulk-change-billing-reservation-list.)
  const visibleSelectionModel = useMemo(() => {
    return filteredRows
      .map(r => r.reservationId)
      .filter((id): id is string => !!id && selectedIds.has(id));
  }, [filteredRows, selectedIds]);

  const hasDelegateChanges = useMemo(
    () => filteredRows.some(r => !isRowBlocked(r) && r.delegateWasReplaced === true),
    [filteredRows]
  );

  const columns: IDataTableColumn<BulkChangeBillingPreviewModel>[] = useMemo(
    () => [
      {
        title: 'Res #',
        valueFactory: row =>
          row.reservationId ? (
            <Link
              to={`/reservations/${row.reservationId}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              {row.recordNumber ?? '—'}
            </Link>
          ) : (
            <Typography variant="body2" className={styles.cellNoWrap}>
              {row.recordNumber ?? '—'}
            </Typography>
          ),
        colSpan: 0.5,
      },
      {
        title: 'Guest',
        valueFactory: row => (
          <Typography variant="body2" className={styles.cellNoWrap}>
            {row.guestName ?? '—'}
          </Typography>
        ),
      },
      {
        title: 'Check In',
        valueFactory: row => (
          <DateDisplay
            date={row.checkInDate ? new Date(row.checkInDate) : undefined}
            dateFormat="M/d/yyyy"
            variant="body2"
            className={styles.cellNoWrap}
          />
        ),
        colSpan: 0.75,
      },
      {
        title: 'Check Out',
        valueFactory: row => (
          <DateDisplay
            date={row.checkOutDate ? new Date(row.checkOutDate) : undefined}
            dateFormat="M/d/yyyy"
            variant="body2"
            className={styles.cellNoWrap}
          />
        ),
        colSpan: 0.75,
      },
      {
        title: 'Room',
        valueFactory: row => (
          <Typography variant="body2" className={styles.cellNoWrap}>
            {row.roomTypeName ?? '—'}
          </Typography>
        ),
        colSpan: 0.75,
      },
      {
        title: 'New Rates',
        valueFactory: row => <NewRatesCell row={row} effectiveStartDate={effectiveStartDate} />,
        align: 'right' as const,
      },
      {
        title: 'Charged',
        name: 'Charged',
        valueFactory: row => <CurrencyCell row={row} value={row.refundAmount} />,
        align: 'right' as const,
        colSpan: 0.75,
      },
      {
        title: 'New Charge',
        name: 'NewCharge',
        valueFactory: row => <CurrencyCell row={row} value={row.newChargeAmount} />,
        align: 'right' as const,
        colSpan: 0.75,
      },
      {
        title: 'Net',
        name: 'Net',
        valueFactory: row => <NetCell row={row} />,
        align: 'right' as const,
        colSpan: 0.75,
      },
      {
        title: 'Status',
        valueFactory: row => <StatusCell row={row} />,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [styles, effectiveStartDate]
  );

  return (
    <>
      <DataTable
        items={filteredRows}
        columns={columns}
        getItemKey={row => row.reservationId ?? ''}
        stickyHeader
        zebra={false}
        tableClass={styles.previewTable}
        rowClass={row => (isRowBlocked(row) ? styles.blockedRow : '')}
        checkboxSelection
        isRowSelectable={row => !isRowBlocked(row)}
        selectionModel={visibleSelectionModel}
        onSelectionModelChange={(model: GridSelectionModel) => {
          // Preserve selections in rows hidden by the active tile filter.
          // The grid's selection model only reflects currently-visible rows; merge with
          // any previously-selected IDs that aren't in the visible set.
          const visibleIds = new Set(
            filteredRows.map(r => r.reservationId).filter((id): id is string => !!id)
          );
          const next = new Set<string>();
          selectedIds.forEach(id => {
            if (!visibleIds.has(id)) next.add(id);
          });
          model.forEach(id => next.add(id as string));
          onSelectionChange(next);
        }}
      />
      {hasDelegateChanges && (
        <div className={styles.legend}>
          <FontAwesomeIcon icon="user-edit" className={styles.legendIcon} />
          <span>Delegate will be changed for this reservation</span>
        </div>
      )}
    </>
  );
};
